import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LlmProviderService } from '../../../../Services/llmProvider.service';
import { SystemService } from '../../../../Services/system.service';
import { Message } from '../../../../Model/System/Message';
import {
  LLMConnectionTestResult,
  LLMProvider,
  LLMProviderNames,
  LLMProviderSettings
} from '../../../../Model/AI/LLMProvider';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
    selector: 'llm-provider-settings',
    templateUrl: 'llmProviderSettings.component.html',
    styleUrls: ['llmProviderSettings.component.css'],
    standalone: false
})
export class LlmProviderSettingsComponent implements OnInit {
  public settings: LLMProviderSettings[] = [];
  public providers = LLMProviderNames;
  public editing: LLMProviderSettings = null;
  public editorVisible = false;
  public saving = false;
  public saveError: string = null;
  public testResult: LLMConnectionTestResult = null;
  private editingHasStoredToken = false;

  constructor(
    private llmProviderService: LlmProviderService,
    private systemService: SystemService,
    private translateService: TranslateService) {
    this.onEdit = this.onEdit.bind(this);
    this.onTest = this.onTest.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.isTestable = this.isTestable.bind(this);
  }

  ngOnInit(): void {
    this.reload();
  }

  public get isAzure(): boolean {
    return this.editing?.provider === LLMProvider.AzureOpenAI;
  }

  public get hasSystemToken(): boolean {
    return !!this.editing?.token || this.editingHasStoredToken;
  }

  public get tokenHelpText(): string {
    return this.translateService.instant(
      'Leer lassen, wenn nextPIM keine eigenen Zugangsdaten hinterlegt und nur Kunden mit eigenem Token diesen Anbieter nutzen sollen');
  }

  public get supportsEmbeddings(): boolean {
    return this.editing?.provider === LLMProvider.OpenAI || this.editing?.provider === LLMProvider.AzureOpenAI;
  }

  public get modelHelpText(): string {
    return this.isAzure
      ? this.translateService.instant('Name der Bereitstellung (Deployment) aus dem Azure-Portal, nicht der Modellname')
      : this.translateService.instant('Modellname des Anbieters, z. B. gpt-4o-mini');
  }

  public isTestable(event: any): boolean {
    return !!event?.row?.data?.token;
  }

  public isCustomerOwnedOnly(settings: LLMProviderSettings): boolean {
    return !settings?.token;
  }

  public mask(token: string): string {
    if (!token) {
      return '';
    }

    return token.startsWith('••••') ? token : `••••${token.slice(-4)}`;
  }

  public onAdd(): void {
    this.editing = {
      id: EMPTY_GUID,
      provider: LLMProvider.OpenAI,
      token: '',
      baseUrl: '',
      defaultChatModel: '',
      defaultEmbeddingModel: '',
      isDefault: false,
      isEmbeddingProvider: false
    };
    this.editingHasStoredToken = false;
    this.saveError = null;
    this.editorVisible = true;
  }

  public onFieldDataChanged(event: any): void {
    if (event.dataField !== 'provider' && event.dataField !== 'token') {
      return;
    }

    if (!this.supportsEmbeddings || !this.hasSystemToken) {
      this.editing.isEmbeddingProvider = false;
    }

    if (!this.supportsEmbeddings) {
      this.editing.defaultEmbeddingModel = '';
    }

    if (!this.hasSystemToken) {
      this.editing.isDefault = false;
    }
  }

  public onEdit(event: any): void {
    this.editing = { ...event.row.data, token: '' };
    this.editingHasStoredToken = !!event.row.data.token;
    this.saveError = null;
    this.editorVisible = true;
  }

  public onSave(): void {
    this.saving = true;
    this.saveError = null;

    this.llmProviderService.saveSettings(this.editing).subscribe({
      next: () => {
        this.saving = false;
        this.editorVisible = false;
        this.reload();
      },
      error: response => {
        this.saving = false;
        this.saveError = this.describe(response);
      }
    });
  }

  public onDelete(event: any): void {
    this.llmProviderService.deleteSettings(event.row.data.id).subscribe({
      next: () => this.reload(),
      error: response => this.notifyError('Anbieter konnte nicht gelöscht werden: ', response)
    });
  }

  public onTest(event: any): void {
    this.testResult = null;
    this.llmProviderService.testConnection(event.row.data).subscribe({
      next: result => this.testResult = result,
      error: response => this.notifyError('Verbindungstest fehlgeschlagen: ', response)
    });
  }

  private reload(): void {
    this.llmProviderService.getSettings().subscribe({
      next: settings => this.settings = settings,
      error: response => this.notifyError('Anbieter konnten nicht geladen werden: ', response)
    });
  }

  private notifyError(prefix: string, response: any): void {
    this.systemService.notify(new Message(this.translateService.instant(prefix) + this.describe(response), 'error'));
  }

  private describe(response: any): string {
    return response?.error?.detail ?? response?.error?.title ?? response?.message ?? this.translateService.instant('Unbekannter Fehler');
  }
}
